import React from 'react';
import { X, FileSpreadsheet } from 'lucide-react';
import { useTheme, cn } from '@/ui/theme/ThemeContext';
import { useAuth } from '@/features/auth/AuthContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { TeamGanttImporter } from '../../../features/import/components/TeamGanttImporter';
import { usePresenceTransition } from '@/shared/hooks/usePresenceTransition';

interface ImportExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FOCUSABLE_DIALOG_ELEMENT_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const ImportExportModal: React.FC<ImportExportModalProps> = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const { hasPermission } = useAuth();
  const { t } = useSettings();
  const { shouldRender, isActive } = usePresenceTransition(isOpen, { duration: 200 });
  const [opening, setOpening] = React.useState({ open: isOpen, generation: 0 });
  const currentOpening = React.useRef(opening);
  const openRef = React.useRef(isOpen);
  openRef.current = isOpen;
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  if (opening.open !== isOpen) {
    const next = { open: isOpen, generation: opening.generation + (isOpen ? 1 : 0) };
    currentOpening.current = next;
    setOpening(next);
  }

  // The dialog is modal (aria-modal): move focus into it on open, keep Tab inside it, and hand
  // focus back to the opener on close. Escape is handled by App's keyboard manager.
  React.useEffect(() => {
    if (!isOpen || !shouldRender || typeof document === 'undefined') return undefined;
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const opener = document.activeElement instanceof HTMLElement && !dialog.contains(document.activeElement)
      ? document.activeElement
      : null;
    const focusableElements = () => Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_DIALOG_ELEMENT_SELECTOR))
      .filter((element) => !element.closest('[hidden], [inert], [aria-hidden="true"]'));

    if (!dialog.contains(document.activeElement)) {
      (focusableElements()[0] ?? dialog).focus({ preventScroll: true });
    }

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.defaultPrevented) return;
      const elements = focusableElements();
      const first = elements[0] ?? dialog;
      const last = elements[elements.length - 1] ?? dialog;
      const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (!active || !dialog.contains(active) || elements.length === 0) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      if (opener?.isConnected && (document.activeElement === document.body || dialog.contains(document.activeElement))) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [isOpen, shouldRender]);

  if (!shouldRender) return null;

  const overlayClassName = cn(
    'fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm transition-opacity duration-200 ease-out motion-reduce:transition-none',
    isActive ? 'opacity-100' : 'opacity-0',
  );
  const panelMotionClassName = cn(
    'transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
    isActive ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-2 scale-[0.985] opacity-0',
  );

  if (!hasPermission('system', 'import')) {
    return (
      <div className={overlayClassName} onClick={onClose}>
        <div className={cn(
          "w-full max-w-md rounded-lg p-6 shadow-2xl",
          panelMotionClassName,
          theme.bgPrimary,
          theme.borderPrimary,
          "border"
        )}
        onClick={(event) => event.stopPropagation()}
        >
          <p className={cn("text-center", theme.textSecondary)}>
            {t('You do not have permission to import data.')}
          </p>
          <button
            onClick={onClose}
            className={cn(
              "mt-4 w-full py-2 px-4 rounded-md font-medium",
              theme.button
            )}
          >
            {t('Close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={overlayClassName} onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-export-modal-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={cn(
        "w-full max-w-2xl rounded-lg shadow-2xl focus:outline-none",
        panelMotionClassName,
        theme.bgPrimary,
        theme.borderPrimary,
        "border"
      )}
      >
        {/* Header */}
        <div className={cn("flex items-center justify-between p-4 border-b", theme.borderPrimary)}>
          <h2 id="import-export-modal-title" className={cn("text-lg font-semibold flex items-center gap-2", theme.textPrimary)}>
            <FileSpreadsheet className="w-5 h-5" aria-hidden="true" />
            {t('TeamGantt Import')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Close')}
            title={t('Close')}
            className={cn("p-1 rounded transition-colors", theme.hover)}
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className={cn("text-sm mb-4", theme.textSecondary)}>
            {t('Import TeamGantt CSV files directly to create your project structure:')}
          </p>
          
          <TeamGanttImporter key={opening.generation} onSuccess={() => {
            if (openRef.current && currentOpening.current.generation === opening.generation) onClose();
          }} compactHeader />
        </div>
      </div>
    </div>
  );
};
